import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { searchRecipientCustomFilter } from 'app/global/auto-complete';
import { getFormValidationErrors } from 'app/global/get-form-validation-errors';
import { setOrgnizationData, setRecipientData } from 'app/global/get-recipient-data';
import { GlobalService } from 'app/global/global.service';
import { organizationFields, personFields } from 'app/global/recipientfields';
import { ParseNamePipe } from 'app/pipes/parse-name.pipe';
import { delay, of, Subscription, take, Subject, takeUntil, distinctUntilChanged, debounceTime, firstValueFrom, map, switchMap, filter, combineLatest, startWith } from 'rxjs';
import { DocumentsService } from '../../documents/documents.service';
import { Organization, Orgfield, Personfield, Recipient, RecipientRole, Validations, TemplateRouteEnum } from '../../edit-template/edit-template.dto';
import { HelperService } from '../../edit-template/helper.service';
import { DocumentFieldService } from '../../settings/documentfields/documentfield.service';
import { IMetadataItemDto, IMetadataListDTO, MetaDataTypeEnum } from '../../settings/documentfields/metadata-item.dto';
import { RecipientfieldsService } from '../../settings/recipientfields/recipientfields.service';
import { RecipientModalComponent } from '../recipient-modal/recipient-modal.component';
import { ValidationModalComponent } from '../validation-modal/validation-modal.component';
import { Authentication, Confirmation, InvitationThrough, InvitationThroughEnum } from '../../general-editor-tabs/recipients-tab/recipient-tab.dto';
import { AUTHENTICATION, CONFIRMATION, INVITATION_THROUGH } from '../../general-editor-tabs/recipients-tab/recipient-data';
import { OrganizationService } from '../../contacts/organizations/organization.service';
import { PermissionsService } from 'app/global/permissions/permissions.service';
import { PermissionsEnum } from 'app/global/permissions/permissions.enum';
import { TemplateType } from '../../templates/template.dto';
import { AuthHelperService } from 'app/auth/helpers/auth-helper.service';
import { UserSettingsFacade } from 'app/store/user-settings.facade';
import { UserSettings } from 'app/global/user-settings.dto';
import { Field } from 'app/interfaces/fields';
import { RecipientsService } from '../../contacts/recipients/recipients.service';
import { MetaDataSectionEnum } from '../../settings/documentfields/meta-data.helper';
import { DocumentFieldsFacade } from '../../settings/documentfields/store/documentfields.facade';
import { AuthenticationTypeEnum } from 'app/main/signing-pages/enums/enum';
import { ReusableAttachmentService } from 'app/global/reusable-attachment.service';

@Component({
  selector: 'app-editrole-modal',
  templateUrl: './editrole-modal.component.html',
  styleUrls: ['./editrole-modal.component.scss'],
  providers: [ParseNamePipe]
})
export class EditroleModalComponent implements OnInit, OnDestroy {
  @ViewChild('autocomplete', { static: false }) autocomplete;
  @ViewChild('orgautocomplete', { static: false }) orgautocomplete;
  @ViewChild('fieldautocomplete', { static: false }) fieldautocomplete;
  @Input() role: RecipientRole;
  @Input() orderLength: number;
  @Input() document_id: number;
  @Input('isEditParticipient')
  get isEditParticipient(): boolean {
    return this.editParticipient;
  }
  set isEditParticipient(value: boolean) {
    this.editParticipient = value || false;
  }
  @Input('isEditParticipientFromSigningPage')
  get isEditParticipientFromSigningPage(): boolean {
    return this.editParticipientFromSigningPage;
  }
  set isEditParticipientFromSigningPage(value: boolean) {
    this.editParticipientFromSigningPage = value || false;
  }
  @Input() get template_type(): string {
    return this.setTemplateType;
  }
  set template_type(value: string) {
    this.setTemplateType = value || TemplateType.REGULAR;
  }
  private setTemplateType: string = TemplateType.REGULAR;
  @Input() get forcePhoneNumberValidation(): boolean {
    return this.setForcePhoneNumberValidation;
  }
  set forcePhoneNumberValidation(value: boolean) {
    this.setForcePhoneNumberValidation = value || false;
  }
  private setForcePhoneNumberValidation: boolean = false;
  public submitted: boolean = false;
  public editParticipient: boolean = false;
  public editParticipientFromSigningPage: boolean = false;
  public recipientRoleForm: FormGroup;
  public readonly: boolean = false;
  public searchable: boolean = false;
  public selectedInvitationthrough: string = 'email';
  public metaData: IMetadataItemDto[] = [];
  public customFieldObject: Partial<IMetadataItemDto>;
  public invitationthrough: InvitationThrough[] = INVITATION_THROUGH();
  public selectedInvitationOrder = 1;
  public invitationOrder: number[] = [];
  public selectedAuthentication: string = 'e_signature';
  public authentication: Authentication[] = AUTHENTICATION();
  public selectedConfirmation: string = 'email';
  public confirmation: Confirmation[] = CONFIRMATION();
  public authenticationRequired: boolean = true;
  public customPeronField: string = '';
  public activeFieldTab: string = 'recipient';
  public metadata_keyword: string = 'meta_data_name';
  public loading: boolean = false;
  public newRoleLoading: boolean = false;
  public activeTab: number = 0;
  public specificRoleObject: RecipientRole = {};
  public phone_no_index: string = '0';
  public extraFieldsId: string[] = [];
  public keyword: string = 'name';
  public recipintSearch = [];
  public organizationSearch: Organization[] = [];
  public autoCompleteLoaders = { recloader: false, orgloader: false, fields: false };
  public customFieldModalRef: NgbModalRef;
  public timeOutSubscription: Subscription;
  public updateParticipantLoader: boolean = false;
  public canUpdateSigningMethod$ = this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_edit_signing_method_on_signing_page);
  private _userSettings$ = this._userSettingsFacade$._userSettings$;
  public _userEmail: string;
  private changesDetect: boolean = false;
  private destroyed = new Subject<void>();
  public metaDataSectionEnum = MetaDataSectionEnum;
  private enforcePhoneRequiredFromExternalRules = false;

  /**
   * Constructor
   * @param _coreSidebarService 
   * @param modalService 
   * @param _formBuilder 
   * @param recipientfieldsService 
   * @param globalService 
   * @param activeModal 
   * @param parseName 
   * @param router 
   * @param documentFieldService 
   */
  constructor(
    private modalService: NgbModal,
    private _formBuilder: FormBuilder,
    private recipientfieldsService: RecipientfieldsService,
    private organizationService: OrganizationService,
    private globalService: GlobalService,
    public activeModal: NgbActiveModal,
    private parseName: ParseNamePipe,
    private documentFieldService: DocumentFieldService,
    private helperService: HelperService,
    private documentsService: DocumentsService,
    private recipientsService: RecipientsService,
    private _permissionService: PermissionsService,
    private _authHelper: AuthHelperService,
    private _userSettingsFacade$: UserSettingsFacade,
    private documentFieldsFacade: DocumentFieldsFacade,
    private reusableAttachmentService: ReusableAttachmentService
  ) { }

  async ngOnInit() {
    this.specificRoleObject = { ...this.role };
    this.invitationOrder = Array.from({ length: this.orderLength }, (x, i) => i + 1);
    this.initForm(this.role);
    this._initializeUserEmail();
    this.getCustomFields();
  }

  private async _initializeUserEmail(): Promise<void> {
    this._userEmail = await firstValueFrom(
      this._userSettings$.pipe(
        take(1),
        map((userSettings: UserSettings) => userSettings?.email)
      )
    );
  }

  /**
   * This function will get person and org custom fields
   */
  getCustomFields() {
    this.documentFieldsFacade.contactFields$
      .pipe(
        take(1),
        switchMap(metaData => {
          if (metaData.length > 0) {
            return of(metaData);
          }
          this.documentFieldsFacade.loadContactFields();
          return this.documentFieldsFacade.contactFields$.pipe(
            filter(loadedMetaData => loadedMetaData.length > 0),
            take(1)
          );
        }),
        debounceTime(300)
      )
      .subscribe({
        next: (customFields: IMetadataItemDto[]) => {
          const fieldSections = {
            person_field: 'recipient',
            org_field: 'org'
          };
          Object.entries(fieldSections).forEach(([sectionKey, formKey]) => {
            const filteredFields = customFields.filter(f => f.section === sectionKey);
            const existingFields = formKey === 'recipient' ? this.personfields.value : this.orgfields.value;

            filteredFields.forEach(field => {
              const alreadyExists = existingFields.some(f => Number(f.metadata_id) === Number(field.id));
              if (!alreadyExists) {
                this.mapCustomField(formKey, field);
              }
            });
          });
        }
      });
  }

  /**
   * This function will map custom field in org and recipient role
   * @param section 
   * @param field 
   */
  mapCustomField(section: string, field: IMetadataItemDto) {
    const sanitizedKey = String(field.meta_data_name)
      .replace(/[^A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9.,?!@#&()\-'"%;:^$]/g, '_')
      .toLowerCase();

    let filedValue = '';
    if (this.specificRoleObject?.recipient?.custom_fields) {
      const found = this.specificRoleObject.recipient.custom_fields.find(f => f.meta_data_id === field.id);
      if (found) {
        filedValue = this.globalService.getContactCustomFieldValue(found);
      }
    }

    const metadataField = this._formBuilder.group({
      id: null,
      is_enable: 0,
      key: sanitizedKey,
      name: field.meta_data_name,
      hint: field.hint,
      metadata_type: field.meta_data_type,
      metadata_id: field.id,
      value: filedValue,
      required: 0,
      validation: 0,
      is_default: 0,
      validations: this.globalService.validationFormGroup({}),
      validation_title: null,
      hidden: 0,
    });
    if (section === 'recipient') {
      this.personfields.push(metadataField);
    } else {
      this.orgfields.push(metadataField);
    }
  }


  // modal Open Form
  customFieldOpenModal(customFieldModal) {
    this.customFieldModalRef = this.modalService.open(customFieldModal);
  }


  recipientCustomFilter(items: Recipient[], query: string) {
    return searchRecipientCustomFilter(items, query);
  }

  addNewRecipient(updateRecipient) {
    if (this.autocomplete) {
      this.autocomplete.close();
      // this.autocomplete.clear();
    }
    this.recipientModal(updateRecipient);
  }

  recipientModal(updateRecipient: boolean, navigation = "recipient", index: number = 0) {
    const recipientRef: NgbModalRef = this.modalService.open(RecipientModalComponent, {
      backdrop: 'static',
      windowClass: 'edit-recipient-modal'
    });
    recipientRef.componentInstance._recipientRole = this.recipientRoleForm.value;
    recipientRef.componentInstance._updateRecipient = updateRecipient;
    recipientRef.componentInstance._navigation = navigation;
    recipientRef.componentInstance._activeIndex = index;
    recipientRef.result.then((result) => {
      if (result) {
        this.selectRecipient(result.recipient, result.personfields, result.orgfields);
      }
    });
  }

  /**
   * Init form with role data
   * @param data 
   */
  initForm(data: RecipientRole) {
    this.recipientRoleForm = this.helperService.getRole(data, false, false);
    this.enforcePhoneRequiredFromExternalRules = this.isPhoneFieldAlreadyRequired();
    this.recipientRoleForm.valueChanges.pipe(takeUntil(this.destroyed), take(1)).subscribe(() => {
      this.changesDetect = true;
    });

    this.roleRequiresPhoneNumberInitialization();

    if (this.editParticipient) {
      this.applyValidation();

      this.recipientRoleForm.get('personfields').valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
      ).subscribe((personfields) => {
        const currentAttachments = this.recipientRoleForm.get('recipient')?.value?.custom_fields;
        const recipient = {
          ...setRecipientData(personfields),
          custom_fields: currentAttachments
        };
        this.recipientRoleForm.patchValue({ recipient });
      });

      this.recipientRoleForm.get('orgfields').valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
      ).subscribe((orgfields) => {
        const currentAttachments = this.recipientRoleForm.get('organization')?.value?.custom_fields;
        const organization = {
          ...setOrgnizationData(orgfields),
          custom_fields: currentAttachments
        };
        this.recipientRoleForm.patchValue({ organization });
      });

      //should be disabled when editing participient from document section or sender link
      this.recipientRoleForm.get('invitation_order').disable();
    }
  }

  /**
   * Update recipient name in person fields controls
   * @param $event 
   * @param personFieldIndex 
   */
  updateRecipientName($event: MouseEvent, personFieldIndex: number) {
    const value = ($event.target as HTMLInputElement).value;
    const { firstname, lastname } = this.parseName.transform(value);
    this.personfields.controls[personFieldIndex].patchValue({ value: firstname });
    this.personfields.controls[personFieldIndex + 1].patchValue({ value: lastname });
  }




  /** 
  * Convenience getter for easy access to form fields 
  */
  get form() {
    return this.recipientRoleForm.controls;
  }



  /**
   * Get person fields
   * @access
   */
  get personfields() {
    return this.recipientRoleForm?.controls["personfields"] as FormArray;
  }



  /**
   * Get organizations fields
   * @access
   */
  get orgfields() {
    return this.recipientRoleForm?.controls["orgfields"] as FormArray;
  }



  /**
   * On required check make values default
   * @param i 
   * @param type 
   */
  onIsEnableChange(i: number, type: string) {
    if (type == 'recipient') {
      this.personfields.controls[i].patchValue(
        {
          required: 0,
          validation: 0,
          validations: this.globalService.validationFormGroup({}).value,
          validation_title: null,
        })
      this.personfields.controls[i].get('value').clearValidators()
      this.personfields.controls[i].get('value').updateValueAndValidity();
    } else if (type == 'org') {
      this.orgfields.controls[i].patchValue(
        {
          required: 0,
          validation: 0,
          validations: this.globalService.validationFormGroup({}).value,
          validation_title: null,
        });
      this.orgfields.controls[i].get('value').clearValidators()
      this.orgfields.controls[i].get('value').updateValueAndValidity();
      if (this.orgfields.controls[i].get('name').value == 'Organization') {
        this.orgfields.controls.forEach((field) => field.get('name').value != 'Organization' && field.get('is_enable').setValue(false));
      }
    }
  }


  onRequiredChange($event, key: string) {
    if (key === 'phone_no'&& this.setForcePhoneNumberValidation) {
      const isRequired = $event.target.checked;
      this.helperService.setPhoneNumberValidation(
        this.recipientRoleForm,
        isRequired
      );
    }
  }


  /**
   * Save recipient role
   * @returns null
   */
  onSaveRecipientRole() {
    this.submitted = true;
    if (this.recipientRoleForm.invalid) {
      getFormValidationErrors(this.personfields, this.globalService);
      getFormValidationErrors(this.orgfields, this.globalService);
      return;
    }

    const personFields = this.recipientRoleForm.value?.personfields;
    personFields.forEach(item => {
      if (item?.key === "phone_no" && item?.value?.e164Number) {
        const country = item?.value?.countryCode ? String(item.value.countryCode).toLowerCase() : null;
        item.value = item?.value?.e164Number;
        item.landcode = country;
      }
    });
    const recipient = this.recipientRoleForm?.get('recipient')?.value;
    if (recipient && recipient?.phone_no?.e164Number) {
      recipient.phone_no = recipient?.phone_no?.e164Number;
    }
    this.recipientRoleForm?.get('recipient')?.setValue(recipient);

    this.recipientRoleForm.patchValue({ personfields: personFields });
    if (this.editParticipient && !this.isEditParticipientFromSigningPage) {
      this.updateParticipant();
    } else {
      this.activeModal.close({ form: this.recipientRoleForm.value, extraFieldsId: this.extraFieldsId, changesDetect: this.changesDetect });
    }
    this.extraFieldsId = [];
  }


  closeModal() {
    this.activeModal.close({ form: this.recipientRoleForm?.value, extraFieldsId: this.extraFieldsId, changesDetect: false });
  }


  /**
   * Add person custom field
   * @param type 
   * @param modelInstances 
   */
  addPersonCustomField(modelInstances) {
    if (Object.keys(this.customFieldObject).length) {
      const personfeild = this._formBuilder.group({
        id: null,
        is_enable: 0,
        key: String(this.customFieldObject['meta_data_name']).replace(/[^A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9.,?!@#&()\-'"%;:^$]/g, '_').toLowerCase(),
        name: this.customFieldObject['meta_data_name'],
        hint: this.customFieldObject['hint'],
        metadata_type: this.customFieldObject['meta_data_type'],
        metadata_id: this.customFieldObject['id'],
        value: '',
        required: 0,
        validation: 0,
        is_default: 0,
        validations: this.globalService.validationFormGroup({}),
        validation_title: null,
        hidden: 0,
      });
      modelInstances.close();
      if (this.activeFieldTab == 'recipient') {
        this.personfields.push(personfeild);
      } else if (this.activeFieldTab == 'org') {
        this.orgfields.push(personfeild);
      }
      this.customFieldObject = {};
    } else {
      this.globalService.toastrWarning('Custom field cannot be empty');
    }
  }



  /**
   * Remove person custom field
   * @param i 
   * @param type 
   * @param extrafield 
   */
  removePersonCustomField(i, type: string, extrafield: FormGroup) {
    if (extrafield.value.id != 0) this.extraFieldsId.push(extrafield.value.id);
    if (type == 'recipient') {
      this.personfields.removeAt(i);
    } else if (type == 'org') {
      this.orgfields.removeAt(i);
    }
  }

  /**
   * Init form with specific role
   * @param role 
   * @param i 
   */
  specificRole(role, i) {
    this.activeTab = i,
      this.specificRoleObject = role;
    this.initForm(role);
  }

  /**
   * Select recipient 
   * @param recipient
   */
  selectRecipient(recipient, person = null, orgField = null) {
    const Personfields = person ? person : this.personfields.value;
    const OrgFields = orgField ? orgField : this.orgfields.value;
    if (recipient.id == 0) {
      this.addNewRecipient(false);
      return;
    }
    if (this.autocomplete) {
      this.autocomplete.close();
      this.autocomplete.clear();
    }
    this.clearRecipient();
    const personfields = [];
    const orgfields = [];
    const fields = personFields(recipient, this.globalService);
    const orgFields = organizationFields(recipient.organization, this.globalService);
    this.timeOutSubscription = of(null).
      pipe(
        delay(0),
        take(1)
      ).subscribe(() => {
        Personfields.forEach(personfield => {
          if (personfield?.metadata_id && personfield?.metadata_id !== null && recipient?.custom_fields && recipient?.custom_fields.length > 0) {
            const found = recipient?.custom_fields.find(f => f.meta_data_id === personfield.metadata_id);
            if (found) {
              personfield = { ...personfield, ...{ value: this.globalService.getContactCustomFieldValue(found) } };
            }
          } else {
            const found = fields.find((v) => v.name == personfield.name);
            if (found) {
              personfield = { ...personfield, ...{ value: found.value } };
            }
          }
          personfields.push(personfield);
        });
        OrgFields.forEach(orgfield => {
          if (orgfield?.metadata_id && orgfield?.metadata_id !== null && recipient?.custom_fields && recipient?.custom_fields.length > 0) {
            const found = recipient?.organization?.custom_fields.find(f => f.meta_data_id === orgfield.metadata_id);
            if (found) {
              orgfield = { ...orgfield, ...{ value: this.globalService.getContactCustomFieldValue(found) } };
            }
          } else {
            const found = orgFields.find((v) => v.name == orgfield.name);
            if (found) {
              orgfield = { ...orgfield, ...{ value: found.value } };
            }
          }
          orgfields.push(orgfield);
        });
        this.specificRoleObject['recipient'] = { ...setRecipientData(personfields), custom_fields: recipient?.custom_fields };
        this.specificRoleObject['organization'] = { ...setOrgnizationData(orgfields), custom_fields: recipient?.organization?.custom_fields };
        this.specificRoleObject['recipient_id'] = recipient.id;
        this.specificRoleObject['organization_id'] = recipient.organization_id;
        this.specificRoleObject['personfields'] = personfields;
        this.specificRoleObject['orgfields'] = orgfields;
        this.initForm(this.specificRoleObject);
      })
    this.recipintSearch = [];
  }


  /**
   * Search recipient 
   * @param val 
   * @returns 
   */
  onRecipientSearch(val: string) {
    if (val == '' || this.recipientRoleForm.get('recipient_id').value != null) {
      this.recipintSearch = [];
      return;
    }
    this.autoCompleteLoaders['recloader'] = true;
    this.recipientfieldsService.searchRecipient(val).subscribe({
      next: (recipients) => {
        this.recipintSearch = recipients?.data || [];
        this.autoCompleteLoaders['recloader'] = false;
      },
      error: (err) => { this.globalService.toastrError(err); }
    });

  }

  /**
   * Create Recipient
   * @access
   */
  async clearRecipient() {
    const personfields = [];
    const orgfields = [];
    this.recipintSearch = [];
    this.personfields.controls.forEach((element) => {
      personfields.push({
        ...element.value,
        value: ''
      })
    });
    this.orgfields.controls.forEach((element) => {
      orgfields.push({
        ...element.value,
        value: ''
      })
    });
    this.personfields.reset(personfields);
    this.orgfields.reset(orgfields);
    this.recipientRoleForm.patchValue({ recipient_id: null, organization_id: null, recipient: null, sender_as_role: false });
    if (this.recipientRoleForm.get('id').value) {
      this.reusableAttachmentService.deleteRoleAttachments(this.recipientRoleForm.get('id').value);
    }
  }

  /**
   * Open validation modal with data 
   * @param validationModal 
   * @param data 
   * @param index 
   */
  openValidationModal(form: Validations, index: number, type: string) {
    const validation: NgbModalRef = this.modalService.open(ValidationModalComponent);
    validation.componentInstance.validations = { ...form, ...{ index: index, type: type } };
    validation.result.then((response: { validations: Validations }) => {
      if (response) {
        this.addValidation(response.validations)
      }
    });
  }



  /**
   * Add validation on persons fields and organization field behalf on index
   * @returns
   */
  addValidation(validations: Validations) {
    const fieldIndex = validations['index'];
    if (validations['type'] == 'personfield') {
      this.personfields.controls[fieldIndex].patchValue({
        required: 1,
        validations: validations,
        validation_title: this.globalService.getValidationTitle(validations)
      });
      this.personfields.controls[fieldIndex].updateValueAndValidity();
    }
    else {
      this.orgfields.controls[fieldIndex].patchValue({
        required: 1,
        validations: validations,
        validation_title: this.globalService.getValidationTitle(validations)
      });
      this.orgfields.controls[fieldIndex].updateValueAndValidity();
    }
    this.initForm(this.recipientRoleForm.value);
  }


  /**
   * Select meta data
   * @param item 
   * @returns 
   */
  selectMetaData(item: IMetadataItemDto) {
    if (item.id == 0) {
      const formData = {
        meta_data_name: item.meta_data_name,
        meta_data_type: MetaDataTypeEnum.text,
        section: this.activeFieldTab == 'recipient' ? 'person_field' : 'org_field',
        api_attribute: item.meta_data_name.replace(' ', '_').toLowerCase()
      };
      this.customFieldModalRef.close();
      this.documentFieldService.createDocumentField(formData)
        .subscribe({
          next: (documentfield: IMetadataItemDto) => {
            this.customFieldObject = documentfield;
            this.metaData = [];
            this.addPersonCustomField(this.customFieldModalRef);
          }, error: err => {
            return this.globalService.toastrError(err.message);
          }
        });
    } else {
      this.customFieldObject = item;
      this.addPersonCustomField(this.customFieldModalRef);
    }
  }



  /**
   * Clear input value on clear event
   */
  clearCustomfieldValue() {
    this.metaData = [];
    this.customFieldObject = {};
  }




  /**
   * Search meta data fields behalf on section
   * @param $event 
   */
  async onFieldsSearch($event: string) {
    this.autoCompleteLoaders['fields'] = true;
    let section = 'person_field';
    if (this.activeFieldTab == 'recipient') {
      section = 'person_field';
    } else if (this.activeFieldTab == 'org') {
      section = 'org_field'
    }
    this.documentFieldService.documentfields('all', $event, `section=${section}`).subscribe((fields: IMetadataListDTO) => {
      this.metaData = fields.data || [];
      this.autoCompleteLoaders['fields'] = false;
    });
  }



  /**
   * when searching person fields from meta data
   * @param items 
   * @param query 
   * @returns 
   */
  fieldsCustomfilterRec(items: IMetadataItemDto[], query: string) {
    const results = [];
    items.forEach(
      (item) => {
        if ((item['meta_data_name']).toLowerCase().indexOf(query.toLowerCase()) > -1) {
          results.push(item);
        }
      }
    );
    results.push({ id: 0, meta_data_name: query, query: `Add ${query == '' ? 'New field' : query} as Person field` });
    return results;
  }




  /**
   * When searching organization fields from meta data
   * @param items 
   * @param query 
   * @returns 
   */
  fieldsCustomfilterOrg(items: IMetadataItemDto[], query: string) {
    const results = [];
    items.forEach(
      (item) => {
        if ((item['meta_data_name']).toLowerCase().indexOf(query.toLowerCase()) > -1) {
          results.push(item);
        }
      }
    );
    results.push({ id: 0, meta_data_name: query, query: `Add ${query == '' ? 'New field' : query} as Organization field` });
    return results;
  }



  /**
   * On close autocomplete
   * @param e 
   */
  close() {
    // e.stopPropagation();
    if (this.fieldautocomplete) {
      this.fieldautocomplete.close();
    }
    this.metaData = []
  }

  applyValidation() {
    const organizationId = this.recipientRoleForm.get('organization_id')?.value;
    if (organizationId) {
      this.orgfields.controls.forEach(control => {
        if (control.get('key')?.value === 'organization') {
          control.get('value').setValidators(Validators.required); // Assuming Validators.required is the validator to be added
          control.get('value').updateValueAndValidity(); // Update the validity after adding the validator
        }
      });
    }
  }

  /**
   * update the participant
   */
  updateParticipant() {
    this.updateParticipantLoader = true;
    const data = this.skipCustomFields(this.recipientRoleForm.getRawValue());
    this.documentsService.updateParticipant(this.document_id, data).subscribe({
      next: () => {
        this.updateParticipantLoader = false;
        this.submitted = false;
        this.activeModal.close({ success: true });
      },
      error: (error) => {
        this.updateParticipantLoader = false;
        this.globalService.toastrError(error);
        this.submitted = false;
      }
    })
  }




  private skipCustomFields(recipientRoleForm: any): any {
    if (!recipientRoleForm) {
      return recipientRoleForm;
    }

    // Deep copy to avoid side effects on the original form's value.
    const data = JSON.parse(JSON.stringify(recipientRoleForm));

    if (data.recipient?.custom_fields) {
      delete data.recipient.custom_fields;
    }

    if (data.organization?.custom_fields) {
      delete data.organization.custom_fields;
    }

    return data;
  }




  addSenderAsRecipient(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    if (isChecked) {
      this.recipientsService.markSenderAsRecipient().subscribe({
        next: (result) => {
          this.specificRoleObject['sender_as_role'] = 1;
          this.selectRecipient(result);
        },
        error: (error) => {
          this.globalService.toastrError(error);
        }
      })
    } else {
      this.specificRoleObject['sender_as_role'] = 0;
      this.clearRecipient();
    }
  }


  /**
   * Handles the lock state change for person fields or organization fields.
   * Updates the lock state of the specified field and optionally synchronizes
   * related fields (e.g., first name and last name for person fields).
   *
   * @param i - The index of the field in the corresponding form array.
   * @param type - The type of field to process. Can be either 'personfields' or 'orgfields'.
   *
   * - If `type` is 'personfields':
   *   - Retrieves the field ID and lock state for the specified person field.
   *   - If the field key is 'first_name', it finds the corresponding 'last_name' field,
   *     updates its lock state, and synchronizes the lock state with the backend.
   *   - Updates the lock state of the specified person field in the backend.
   *
   * - If `type` is 'orgfields':
   *   - Retrieves the field ID and lock state for the specified organization field.
   *   - Updates the lock state of the specified organization field in the backend.
   *
   * @remarks
   * This method interacts with the backend to persist the lock state changes
   * using the `lockTheRecipientField` method.
   */
  onLockCheck(i: number, type: string, event: Event): void {
    const fieldValue = (event.target as HTMLInputElement)?.checked || false;
    const field = this?.personfields.controls[i];
    const fieldId = field.get('id').value;
    const lockFieldValue = fieldValue;
    if (type === 'personfields') {
      if (this.personfields.controls[i].get('key').value === 'first_name') {
        const findLastName = this?.personfields.controls.findIndex((field) => field.get('key').value === 'last_name');
        const lastNameControl = this?.personfields.get(`${findLastName}.lock`);
        const lastNameId = this?.personfields.get(`${findLastName}.id`).value;
        lastNameControl.patchValue(lockFieldValue);
        lastNameControl.updateValueAndValidity();
        this.lockTheRecipientField(lastNameId, 'person_field', (lockFieldValue ? 1 : 0), field.value);
      }
      this.lockTheRecipientField(fieldId, 'person_field', (lockFieldValue ? 1 : 0), field.value);
    } else if (type === 'orgfields') {
      this.lockTheRecipientField(fieldId, 'org_field', (lockFieldValue ? 1 : 0), field.value);
    }
  }

  /**
   * Locks or unlocks recipient fields based on the provided parameters.
   *
   * This method searches for fields matching the specified `id` and `type` 
   * within the global service's field data. For each matching field, it updates 
   * the field's lock status to the provided `value`.
   *
   * @param id - The identifier of the field type to match.
   * @param type - The type of the field to match.
   * @param value - The lock value to set (e.g., 1 for locked, 0 for unlocked).
   */
  private lockTheRecipientField(id: number, type: string, value: number, recipientField: Personfield | Orgfield) {
    const findFields = this.globalService.findMatchingField(this.globalService.fields_data, 'selected_field_type_id', id, type);
    findFields.forEach((field: Field) => {
      this.globalService.updateField(field?.uuid, { lock: value, field_required: value === 1 ? 0 : (recipientField.required ? 1 : 0) })
    });
  }

  get roleId(): number {
    return this.recipientRoleForm.get('id').value;
  }

  private roleRequiresPhoneNumberInitialization(): void {
    // Observables with initial value so that combineLatest emits immediately
    const invitation$ = this.recipientRoleForm.get('invitation_type').valueChanges.pipe(
      startWith(this.recipientRoleForm.get('invitation_type').value)
    );
    const auth$ = this.recipientRoleForm.get('authentication_type').valueChanges.pipe(
      startWith(this.recipientRoleForm.get('authentication_type').value)
    );

    combineLatest([invitation$, auth$])
      .pipe(takeUntil(this.destroyed))
      .subscribe(([invitation, authentication]) => {
        const needsPhoneForInvite = [InvitationThroughEnum.EMAIL_AND_SMS, InvitationThroughEnum.SMS]
          .includes(invitation);

        const needsPhoneForAuth = [AuthenticationTypeEnum.ESIGNANDSMSPIN /*, AuthenticationTypeEnum.SMSPIN */]
          .includes(authentication);
        
        if(this.setForcePhoneNumberValidation) {
          this.helperService.setPhoneNumberValidation(
            this.recipientRoleForm,
            needsPhoneForInvite || needsPhoneForAuth || this.enforcePhoneRequiredFromExternalRules
          );
        }
      });
  }

  private isPhoneFieldAlreadyRequired(): boolean {
    const phoneField = this.personfields?.controls?.find((field) => field.get('key')?.value === 'phone_no');
    if (!phoneField) {
      return false;
    }

    return Number(phoneField.get('required')?.value) === 1 && Number(phoneField.get('is_enable')?.value) === 1;
  }

  ngOnDestroy() {
    if (this.timeOutSubscription) {
      this.timeOutSubscription.unsubscribe();
    }
    this.destroyed.next();
    this.destroyed.complete();
  }
}
